import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Star, User, MapPin, Filter, X, ChevronDown } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import HeroSection from "@/components/HeroSection";
import Footer from "@/components/Footer";
import WaveDivider from "@/components/WaveDivider";
import FloatingUserMenu from "@/components/FloatingUserMenu";
import ImageLightbox from "@/components/ImageLightbox";
import SEO from "@/components/SEO";
import ScrollReveal from "@/components/ScrollReveal";
import { generateBreadcrumbSchema } from "@/utils/structuredData";
import { getImageUrl } from "@/utils/imageUtils";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const RenderStars = ({ rating, size = "w-4 h-4" }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star key={star} className={`${size} ${
        star <= rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
      }`} />
    ))}
  </div>
);

const ReviewCard = ({ review, onClick }) => {
  const profilePic = review.profile_picture_url ? getImageUrl(review.profile_picture_url) : null;

  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-white rounded-xl border border-gray-200 p-3 sm:p-4 hover:shadow-lg hover:border-cyan-300 transition-all group"
      data-testid="review-card"
    >
      <div className="flex items-center gap-2 sm:gap-3">
        {profilePic ? (
          <img src={profilePic} alt={review.name}
            className="w-9 h-9 sm:w-11 sm:h-11 rounded-full object-cover border-2 border-cyan-200 shrink-0"
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
        ) : null}
        <div className={`w-9 h-9 sm:w-11 sm:h-11 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center shrink-0 ${profilePic ? 'hidden' : ''}`}>
          <User className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-gray-900 text-xs sm:text-sm truncate">{review.name}</p>
          <p className="text-[10px] sm:text-xs text-gray-500 truncate">{review.country}</p>
        </div>
      </div>

      {/* Stars - always visible */}
      <div className="mt-2">
        <RenderStars rating={review.rating} size="w-3.5 h-3.5 sm:w-4 sm:h-4" />
      </div>

      {/* Tags - hidden on mobile */}
      <div className="hidden sm:flex mt-2.5 flex-wrap gap-1.5">
        {review.program && (
          <span className="inline-block bg-cyan-50 text-cyan-700 text-[11px] px-2 py-0.5 rounded-full font-medium">
            {review.program}
          </span>
        )}
        {review.dive_center && (
          <span className="inline-block bg-blue-50 text-blue-700 text-[11px] px-2 py-0.5 rounded-full font-medium truncate max-w-[160px]">
            {review.dive_center}
          </span>
        )}
        {review.country_dove && (
          <span className="inline-flex items-center gap-0.5 bg-gray-100 text-gray-600 text-[11px] px-2 py-0.5 rounded-full">
            <MapPin className="w-3 h-3" />{review.country_dove}
          </span>
        )}
      </div>

      {/* Program tag on mobile - single line */}
      <div className="sm:hidden mt-1.5">
        {review.program && (
          <span className="inline-block bg-cyan-50 text-cyan-700 text-[10px] px-1.5 py-0.5 rounded-full font-medium truncate max-w-full">
            {review.program}
          </span>
        )}
      </div>

      {/* Text preview + Read more */}
      <p className="mt-2.5 text-sm text-gray-600 line-clamp-2 leading-relaxed">
        {review.review_text}
      </p>
      <p className="mt-2 text-[11px] text-cyan-600 group-hover:text-cyan-700 font-medium">
        Read more
      </p>
    </button>
  );
};

const ReviewDetailModal = ({ review, onClose }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const profilePic = review.profile_picture_url ? getImageUrl(review.profile_picture_url) : null;

  const images = review.image_urls?.length > 0
    ? review.image_urls
    : review.image_url ? [review.image_url] : [];

  return (
    <div className="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4" onClick={onClose} data-testid="review-detail-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between rounded-t-2xl z-10">
          <div className="flex items-center gap-3">
            {profilePic ? (
              <img src={profilePic} alt={review.name}
                className="w-12 h-12 rounded-full object-cover border-2 border-cyan-300"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
              />
            ) : null}
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center"
              style={{ display: profilePic ? 'none' : 'flex' }}>
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">{review.name}</h3>
              <p className="text-sm text-gray-500">{review.country}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <RenderStars rating={review.rating} size="w-6 h-6" />
            <span className="text-xs text-gray-400">
              {new Date(review.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            {review.program && (
              <span className="bg-cyan-50 text-cyan-700 text-xs px-3 py-1 rounded-full font-medium">{review.program}</span>
            )}
            {review.dive_center && (
              <span className="bg-blue-50 text-blue-700 text-xs px-3 py-1 rounded-full font-medium">{review.dive_center}</span>
            )}
            {review.country_dove && (
              <span className="flex items-center gap-1 bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full">
                <MapPin className="w-3 h-3" />{review.country_dove}
              </span>
            )}
          </div>

          <p className="text-gray-700 leading-relaxed whitespace-pre-line">{review.review_text}</p>

          {images.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {images.map((imgUrl, idx) => (
                <img key={idx}
                  src={`${BACKEND_URL}${imgUrl}`}
                  alt={`Review ${idx + 1}`}
                  className="h-28 w-28 rounded-lg object-cover cursor-pointer hover:opacity-90 hover:scale-105 transition-all border border-gray-200 flex-shrink-0"
                  onClick={() => {
                    setLightboxImages(images.map(url => `${BACKEND_URL}${url}`));
                    setLightboxIndex(idx);
                    setLightboxOpen(true);
                  }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <ImageLightbox
        images={lightboxImages}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  );
};

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [filters, setFilters] = useState({
    country_dove: "", dive_center: "", program: "", rating: ""
  });
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API}/reviews`);
        setReviews(response.data);
      } catch (error) {
        console.error("Error loading reviews:", error);
        toast.error("Failed to load reviews");
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  // Build filter options from review data
  const filterOptions = useMemo(() => ({
    countries: [...new Set(reviews.map(r => r.country_dove).filter(Boolean))].sort(),
    centers: [...new Set(reviews.map(r => r.dive_center).filter(Boolean))].sort(),
    programs: [...new Set(reviews.map(r => r.program).filter(Boolean))].sort(),
    ratings: [...new Set(reviews.map(r => r.rating).filter(Boolean))].sort((a, b) => b - a),
  }), [reviews]);

  const filteredReviews = useMemo(() => {
    return reviews.filter(r => {
      if (filters.country_dove && r.country_dove !== filters.country_dove) return false;
      if (filters.dive_center && r.dive_center !== filters.dive_center) return false;
      if (filters.program && r.program !== filters.program) return false;
      if (filters.rating && r.rating !== Number(filters.rating)) return false;
      return true;
    });
  }, [reviews, filters]);

  const hasActiveFilters = Object.values(filters).some(v => v);

  const clearFilters = () => setFilters({ country_dove: "", dive_center: "", program: "", rating: "" });

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: "Home", url: "https://georgedives.com" },
    { name: "Reviews", url: "https://georgedives.com/reviews" }
  ]);

  return (
    <div className="min-h-screen">
      <SEO
        title="Student Reviews & Testimonials | George Dives"
        description="Read reviews from divers who've trained with George."
        keywords="dive reviews, student testimonials, PADI instructor reviews"
        schema={breadcrumbSchema}
      />

      <Navigation />

      <HeroSection
        title="Diver Reviews,"
        subtitle="Real Experiences"
        description="Hear what fellow divers have to say about their underwater adventures."
        backgroundColor="from-cyan-600 via-blue-600 to-purple-700"
      />
      <WaveDivider variant="smooth" />

      <div className="container mx-auto px-4 py-8 max-w-6xl">

        {/* Filters - Collapsible */}
        {reviews.length > 0 && (
          <div className="mb-6 bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="review-filters">
            <button
              type="button"
              onClick={() => setFiltersOpen(!filtersOpen)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
              data-testid="toggle-filters-btn"
            >
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Filter reviews</span>
                {hasActiveFilters && (
                  <span className="bg-cyan-100 text-cyan-700 text-[11px] px-2 py-0.5 rounded-full font-medium">
                    {filteredReviews.length} of {reviews.length}
                  </span>
                )}
              </div>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${filtersOpen ? "rotate-180" : ""}`} />
            </button>
            {filtersOpen && (
              <div className="px-4 pb-4 border-t">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3">
                  <Select value={filters.country_dove} onValueChange={(v) => setFilters(f => ({ ...f, country_dove: v === "__all__" ? "" : v }))}>
                    <SelectTrigger className="h-9 text-sm" data-testid="filter-country">
                      <SelectValue placeholder="Country" />
                    </SelectTrigger>
                    <SelectContent className="bg-white max-h-48">
                      <SelectItem value="__all__">All countries</SelectItem>
                      {filterOptions.countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>

                  <Select value={filters.dive_center} onValueChange={(v) => setFilters(f => ({ ...f, dive_center: v === "__all__" ? "" : v }))}>
                    <SelectTrigger className="h-9 text-sm" data-testid="filter-center">
                      <SelectValue placeholder="Dive Center" />
                    </SelectTrigger>
                    <SelectContent className="bg-white max-h-48">
                      <SelectItem value="__all__">All dive centers</SelectItem>
                      {filterOptions.centers.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>

                  <Select value={filters.program} onValueChange={(v) => setFilters(f => ({ ...f, program: v === "__all__" ? "" : v }))}>
                    <SelectTrigger className="h-9 text-sm" data-testid="filter-program">
                      <SelectValue placeholder="Program" />
                    </SelectTrigger>
                    <SelectContent className="bg-white max-h-48">
                      <SelectItem value="__all__">All programs</SelectItem>
                      {filterOptions.programs.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>

                  <Select value={filters.rating} onValueChange={(v) => setFilters(f => ({ ...f, rating: v === "__all__" ? "" : v }))}>
                    <SelectTrigger className="h-9 text-sm" data-testid="filter-rating">
                      <SelectValue placeholder="Rating" />
                    </SelectTrigger>
                    <SelectContent className="bg-white max-h-48">
                      <SelectItem value="__all__">All ratings</SelectItem>
                      {filterOptions.ratings.map(r => (
                        <SelectItem key={r} value={String(r)}>
                          <span className="flex items-center gap-1">{r} <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" /></span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {hasActiveFilters && (
                  <button onClick={clearFilters} className="mt-2 text-xs text-cyan-600 hover:text-cyan-700 font-medium" data-testid="clear-filters-btn">
                    Clear all filters
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Reviews Grid */}
        {loading ? (
          <div className="text-center text-gray-500 text-lg py-12">
            <div className="animate-pulse">Loading reviews...</div>
          </div>
        ) : filteredReviews.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">
              {hasActiveFilters ? "No reviews match your filters." : "No reviews yet. Be the first to share your experience!"}
            </p>
            {hasActiveFilters && (
              <Button variant="outline" onClick={clearFilters} className="mt-3">Clear filters</Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-testid="reviews-grid">
            {filteredReviews.map((review, idx) => (
              <ScrollReveal key={review.id} animation="fade-up" delay={Math.min(idx * 80, 400)} duration={600}>
                <ReviewCard
                  review={review}
                  onClick={() => setSelectedReview(review)}
                />
              </ScrollReveal>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedReview && (
        <ReviewDetailModal
          review={selectedReview}
          onClose={() => setSelectedReview(null)}
        />
      )}

      <FloatingUserMenu />
      <Footer />
    </div>
  );
};

export default Reviews;
